import Vue from 'vue'

let delayedCalls = []
let SentryMock = {}

let sentryReadyResolve
let loadInitiated = false
let loadCompleted = false

let delayedGlobalErrors = []
let delayedUnhandledRejections = []
/** @param {ErrorEvent} event */
const delayGlobalError = function (event) {
  delayedGlobalErrors.push([event.message, event.filename, event.lineno, event.colno, event.error])
}
const delayUnhandledRejection = function (event) {
  delayedUnhandledRejections.push('reason' in event ? event.reason : 'detail' in event && 'reason' in event.detail ? event.detail.reason : event)
}

const vueErrorHandler = Vue.config.errorHandler

Vue.config.errorHandler = (error, vm, info) => {
  if (!loadCompleted) {
    if (vm) {
      vm.$sentry.captureException(error)
    }

    if (Vue.util) {
      Vue.util.warn(`Error in ${info}: "${error.toString()}"`, vm)
    }
    console.error(error)
  }

  if (vueErrorHandler) {
    return vueErrorHandler(error, vm, info)
  }
}

export default function SentryPlugin (ctx, inject) {
  /* eslint-disable-next-line quotes, comma-spacing */
  const apiMethods = ["init","vueRouterInstrumentation","browserTracingIntegration","attachErrorHandler","createTracingMixins","VueIntegration","vueIntegration","FunctionToString","Hub","InboundFilters","ModuleMetadata","Scope","addBreadcrumb","addEventProcessor","addGlobalEventProcessor","addIntegration","addTracingExtensions","captureEvent","captureException","captureMessage","captureSession","close","configureScope","continueTrace","createTransport","endSession","extractTraceparentData","flush","functionToStringIntegration","getActiveSpan","getActiveTransaction","getClient","getCurrentHub","getCurrentScope","getHubFromCarrier","getSpanStatusFromHttpCode","inboundFiltersIntegration","isInitialized","lastEventId","makeMain","makeMultiplexedTransport","moduleMetadataIntegration","parameterize","setContext","setCurrentClient","setExtra","setExtras","setHttpStatus","setMeasurement","setTag","setTags","setUser","spanStatusfromHttpCode","startInactiveSpan","startSession","startSpan","startSpanManual","startTransaction","trace","withActiveSpan","withIsolationScope","withScope","BrowserClient","makeFetchTransport","makeXHRTransport","defaultStackParser","eventFromException","eventFromMessage","exceptionFromError","createUserFeedbackEnvelope","captureUserFeedback","forceLoad","getDefaultIntegrations","onLoad","showReportDialog","wrap","Breadcrumbs","breadcrumbsIntegration","Dedupe","GlobalHandlers","globalHandlersIntegration","HttpContext","httpContextIntegration","LinkedErrors","linkedErrorsIntegration","TryCatch","browserApiErrorsIntegration","Replay","getReplay","replayIntegration","ReplayCanvas","replayCanvasIntegration","Feedback","feedbackIntegration","sendFeedback","captureConsoleIntegration","contextLinesIntegration","debugIntegration","dedupeIntegration","extraErrorDataIntegration","httpClientIntegration","reportingObserverIntegration","rewriteFramesIntegration","sessionTimingIntegration","BrowserTracing","instrumentOutgoingRequests","startBrowserTracingNavigationSpan","startBrowserTracingPageLoadSpan","makeBrowserOfflineTransport","onProfilingStartRouteTransaction","BrowserProfilingIntegration","browserProfilingIntegration"]
  apiMethods.forEach((key) => {
    SentryMock[key] = (...args) => delayedCalls.push([key, args])
  })

  window.addEventListener('error', delayGlobalError)
  window.addEventListener('unhandledrejection', delayUnhandledRejection)

  inject('sentry', SentryMock)
  ctx.$sentry = SentryMock

  const loadSentryHook = () => attemptLoadSentry(ctx, inject)

  window.onNuxtReady(loadSentryHook)

  const sentryReadyPromise = new Promise((resolve) => {
    sentryReadyResolve = resolve
  })

  const sentryReady = () => sentryReadyPromise

  inject('sentryReady', sentryReady)
  ctx.$sentryReady = sentryReady
}

async function attemptLoadSentry (ctx, inject) {
  if (loadInitiated) {
    return
  }

  loadInitiated = true

  if (!window.$nuxt) {
    // Wait for onNuxtReady hook to trigger.

    return
  }

  await loadSentry(ctx, inject)
}

async function loadSentry (ctx, inject) {
  if (loadCompleted) {
    return
  }

  const Sentry = await import(/* webpackChunkName: 'sentry' */ '~@sentry/vue')

  const { ExtraErrorData, ReportingObserver, RewriteFrames } = await import(/* webpackChunkName: 'sentry' */ '~@sentry/integrations')
  const { InboundFilters, Breadcrumbs } = Sentry.Integrations

  /* eslint-disable quotes, key-spacing */
  const config = {
    Vue,
    release: "84960152834f54993d281c177e4cd77f7dab334e",
    dsn: "https:\u002F\u002F4eeca9c8f0a8be432b8a1ea0380d7a8e@sentry.despatchcloud.com\u002F12",
    environment: "production",
  }

  const runtimeConfigKey = "sentry"
  if (ctx.$config && runtimeConfigKey && ctx.$config[runtimeConfigKey]) {
    const { default: merge } = await import(/* webpackChunkName: 'sentry' */ '~lodash.mergewith')
    merge(config, ctx.$config[runtimeConfigKey].config, ctx.$config[runtimeConfigKey].clientConfig)
  }

  config.integrations = [
    new ExtraErrorData(),
    new ReportingObserver(),
    new RewriteFrames(),
    new Breadcrumbs(),
    new InboundFilters(),
  ]

  Sentry.init(config)

  loadCompleted = true

  window.removeEventListener('error', delayGlobalError)
  window.removeEventListener('unhandledrejection', delayUnhandledRejection)
  if (delayedGlobalErrors.length) {
    if (window.onerror) {
      console.info('Reposting global errors after Sentry has loaded')
      for (const errorArgs of delayedGlobalErrors) {
        window.onerror.apply(window, errorArgs)
      }
    }
    delayedGlobalErrors = []
  }
  if (delayedUnhandledRejections.length) {
    if (window.onunhandledrejection) {
      console.info('Reposting unhandled promise rejection errors after Sentry has loaded')
      for (const reason of delayedUnhandledRejections) {
        window.onunhandledrejection(reason)
      }
    }
    delayedUnhandledRejections = []
  }
  delayedCalls.forEach(([methodName, args]) => Sentry[methodName].apply(Sentry, args))

  forceInject(ctx, inject, 'sentry', Sentry)

  sentryReadyResolve(Sentry)

  // help gc

  // Dont unset delayedCalls & SentryMock during
  // development, this will cause HMR issues

  delayedCalls = undefined
  SentryMock = undefined

  sentryReadyResolve = undefined
}

// Custom inject function that is able to overwrite previously injected values,
// which original inject doesn't allow to do.
// This method is adapted from the inject method in nuxt/vue-app/template/index.js
function forceInject (ctx, inject, key, value) {
  inject(key, value)
  const injectKey = '$' + key
  ctx[injectKey] = value
  window.$nuxt.$options[injectKey] = value
}
